import { useEffect, useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import api from '../services/apiClient';
import useAuthStore from '../store/authStore';
import { Button, Field, Input, Select } from '../components/ui';
import { toast } from '../components/toast';
import CompanyLogo from '../components/CompanyLogo';

const TIMEZONES = ['America/Sao_Paulo', 'America/Manaus', 'America/Recife', 'America/Fortaleza', 'UTC'];
const EMPTY = { name: '', timezone: 'America/Sao_Paulo', metricoolBlogId: '' };

export default function Companies() {
  const reloadCompanies = useAuthStore((s) => s.reloadCompanies);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (novo) | company

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('/companies', { limit: 100 });
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Persiste a empresa e retorna o id (usado pelo CompanyForm para depois subir o logo)
  const persist = async (form) => {
    if (form.id) {
      await api.put(`/companies/${form.id}`, form);
      return form.id;
    }
    const created = await api.post('/companies', form);
    return created.id;
  };

  const afterSave = async () => {
    setEditing(null);
    await load();
    await reloadCompanies();
  };

  return (
    <div>
      <div className="page-actions">
        <Button onClick={() => setEditing({ ...EMPTY })}><Plus size={16} /> Nova empresa</Button>
      </div>

      {loading ? (
        <p className="muted">Carregando…</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr><th>Nome</th><th>Fuso</th><th>Metricool (blogId)</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="muted center">Nenhuma empresa ainda.</td></tr>}
            {items.map((c) => (
              <tr key={c.id}>
                <td>
                  <span className="row" style={{ gap: 10 }}>
                    <CompanyLogo url={c.logoUrl} name={c.name} size={30} />
                    <strong>{c.name}</strong>
                  </span>
                </td>
                <td className="muted">{c.timezone}</td>
                <td>{c.metricoolBlogId || <span className="badge gray">não vinculado</span>}</td>
                <td style={{ textAlign: 'right' }}>
                  <Button variant="ghost" className="sm" onClick={() => setEditing(c)}><Pencil size={14} /> Editar</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && <CompanyForm initial={editing} onClose={() => setEditing(null)} onPersist={persist} onDone={afterSave} />}
    </div>
  );
}

function CompanyForm({ initial, onClose, onPersist, onDone }) {
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || '',
    timezone: initial.timezone || 'America/Sao_Paulo',
    metricoolBlogId: initial.metricoolBlogId || ''
  });
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(initial.logoUrl || null); // logo ja existente
  const [logoFile, setLogoFile] = useState(null); // arquivo pendente (no cadastro)
  const [logoPreview, setLogoPreview] = useState(null); // preview local do arquivo pendente
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (form.id && !logoUrl) {
      api.get(`/companies/${form.id}`).then((c) => setLogoUrl(c.logoUrl || null)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [form.id]);

  const pickLogo = async (file) => {
    if (!file) return;
    if (form.id) {
      // empresa ja existe: sobe na hora
      try {
        const res = await api.upload(`/companies/${form.id}/logo`, file);
        setLogoUrl(res.logoUrl);
        toast.success('Logo enviado');
      } catch (err) { toast.error(err.message); }
    } else {
      // cadastro novo: segura o arquivo para subir apos criar
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const id = await onPersist(form);
      if (!form.id && logoFile) {
        try { await api.upload(`/companies/${id}/logo`, logoFile); } catch (err) { toast.error('Empresa criada, mas falhou o logo: ' + err.message); }
      }
      toast.success(form.id ? 'Empresa atualizada' : 'Empresa criada');
      await onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const shownLogo = logoPreview || logoUrl;

  return (
    <div className="card mt16" style={{ maxWidth: 520 }}>
      <h3 style={{ marginTop: 0 }}>{form.id ? 'Editar empresa' : 'Nova empresa'}</h3>
      <form onSubmit={submit}>
        <Field label="Nome"><Input value={form.name} onChange={set('name')} required autoFocus /></Field>
        <Field label="Fuso horário">
          <Select value={form.timezone} onChange={set('timezone')}>
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </Select>
        </Field>
        <Field label="Metricool blogId" hint="Vincule depois em Conexões, se preferir">
          <Input value={form.metricoolBlogId} onChange={set('metricoolBlogId')} placeholder="ex: 1234567" />
        </Field>

        <Field label="Logo da marca" hint="Aparece no sistema e é usado nas artes geradas pela IA. PNG com fundo transparente fica melhor.">
          <div className="row" style={{ gap: 12 }}>
            {shownLogo
              ? <img src={shownLogo} alt="Logo" style={{ width: 64, height: 64, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }} />
              : <div style={{ width: 64, height: 64, border: '1px dashed var(--border)', borderRadius: 8, display: 'grid', placeItems: 'center', color: 'var(--muted)', fontSize: 11 }}>sem logo</div>}
            <label className="btn secondary sm" style={{ cursor: 'pointer', margin: 0 }}>
              {shownLogo ? 'Trocar logo' : 'Selecionar logo'}
              <input type="file" accept="image/*" hidden
                onChange={(e) => { pickLogo(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
          </div>
        </Field>

        <div className="row mt8">
          <Button type="submit" loading={saving}>Salvar</Button>
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
        </div>
      </form>
    </div>
  );
}
